/**
 * MountWallHQCard — discoverability + frictionless tablet pinning
 * ===============================================================
 *
 * Lives on the Dashboard (`/app`) so a logged-in homeowner can either:
 *   1. Click "Open Wall HQ" to launch /widget on this device, or
 *   2. Scan a QR with their phone/iPad/Fire tablet to pin /widget there.
 *
 * Auth model: the kiosk uses cookie auth. Once the user logs in on the
 * tablet (one-time), the refresh-token cookie keeps it alive ~7d.
 */
import React, { useMemo, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Monitor, Copy, ArrowRight, CheckCircle, X } from '@phosphor-icons/react';

const WIDGET_PATH = '/widget';

function buildWidgetUrl() {
  if (typeof window === 'undefined') return WIDGET_PATH;
  return `${window.location.origin}${WIDGET_PATH}`;
}

export const MountWallHQCard = () => {
  const [copied, setCopied] = useState(false);
  const url = useMemo(buildWidgetUrl, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Fallback: select the URL in a temporary textarea
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch {}
      document.body.removeChild(ta);
    }
  };

  return (
    <Card
      className="border-2 border-emerald-500/30 bg-card text-foreground overflow-hidden"
      data-testid="mount-wall-hq-card"
    >
      <CardContent className="p-5 sm:p-6">
        <div className="flex flex-col sm:flex-row gap-5 sm:gap-6 items-start">
          {/* Left: copy + actions */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <Monitor weight="duotone" size={18} className="text-emerald-600 dark:text-emerald-400" />
              <span className="text-[10px] uppercase tracking-[0.25em] text-emerald-700 dark:text-emerald-400 font-semibold">
                Wall HQ · Always-on display
              </span>
            </div>
            <h3 className="text-xl sm:text-2xl font-semibold text-foreground mb-2 leading-tight">
              Mount TurfPulse on your iPad, Fire tablet, or wall TV
            </h3>
            <p className="text-sm text-foreground/80 mb-4 max-w-xl">
              Glanceable spray-window, next irrigation, GDD, soil temp, ET₀ and disease pressure —
              auto-refreshes every 5 min. Sign in once on the tablet; it stays live for ~7 days.
            </p>

            <div className="flex flex-wrap items-center gap-2 mb-4">
              <Button
                onClick={() => window.open(WIDGET_PATH, '_blank', 'noopener')}
                className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold"
                data-testid="open-wall-hq-btn"
              >
                Open Wall HQ
                <ArrowRight size={16} weight="bold" className="ml-1" />
              </Button>
              <Button
                onClick={copy}
                variant="outline"
                className="border-border text-foreground hover:bg-muted"
                data-testid="copy-wall-hq-url-btn"
              >
                {copied ? (
                  <>
                    <CheckCircle size={16} weight="fill" className="mr-1 text-emerald-500" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy size={16} className="mr-1" />
                    Copy URL
                  </>
                )}
              </Button>
            </div>

            <ol className="text-xs text-muted-foreground space-y-1 max-w-lg">
              <li>1. Scan the QR with your tablet, or paste the copied URL.</li>
              <li>2. Sign in on the tablet (one-time).</li>
              <li>3. Add to Home Screen → tap to launch full-screen kiosk.</li>
            </ol>
          </div>

          {/* Right: QR */}
          <div className="flex flex-col items-center gap-2 self-center sm:self-start">
            <div className="bg-white p-3 rounded-xl shadow-md border border-border" data-testid="wall-hq-qr">
              <QRCodeSVG
                value={url}
                size={132}
                bgColor="#ffffff"
                fgColor="#0f172a"
                level="M"
                marginSize={0}
              />
            </div>
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
              Scan to pin
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * Compact "Get QR" button + popover modal — used inside /widget itself so
 * the user can flash the QR onto a phone screen and scan it from another
 * device (e.g. from desktop → tablet without re-typing the URL).
 */
export const WallHQQrPopover = () => {
  const [open, setOpen] = useState(false);
  const url = useMemo(buildWidgetUrl, []);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-[11px] uppercase tracking-widest text-zinc-500 hover:text-emerald-400 transition-colors px-2 py-1 rounded border border-zinc-800 hover:border-emerald-500/40"
        data-testid="widget-show-qr-btn"
      >
        Pin on tablet
      </button>
      {open ? (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
          data-testid="widget-qr-modal"
        >
          <div
            className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 max-w-sm w-full text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-end -mt-2 -mr-2 mb-1">
              <button
                onClick={() => setOpen(false)}
                className="text-zinc-500 hover:text-zinc-200 p-1"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-400 mb-2">
              Pin on tablet
            </div>
            <h3 className="text-lg font-semibold text-zinc-100 mb-3">
              Scan to mount Wall HQ
            </h3>
            <div className="inline-block bg-white p-3 rounded-xl shadow-lg mb-3">
              <QRCodeSVG value={url} size={184} bgColor="#ffffff" fgColor="#0f172a" level="M" marginSize={0} />
            </div>
            <p className="text-xs text-zinc-500 break-all mb-3">{url}</p>
            <p className="text-xs text-zinc-400">
              Sign in once on the tablet. Use "Add to Home Screen" for full-screen kiosk mode.
            </p>
          </div>
        </div>
      ) : null}
    </>
  );
};
